import { useState, useEffect, useCallback } from 'react';
import PageHeader from '../components/PageHeader';
import Button from '../components/ui/Button';
import publicHolidayService, { type PublicHoliday } from '../services/publicHoliday';
import { AUSTRALIAN_STATES } from '../services/user';
import { CalendarDays, Plus, Trash2, Download, Pencil } from 'lucide-react';
import { useOrgTimezone, formatLocalDateInTz } from '@/utils/timezone';
import { useModuleLicense } from '../hooks/useModuleLicense';
import { cn } from '@/utils/cn';

const CURRENT_YEAR = new Date().getFullYear();

/** What the add and edit forms hold. No states means everyone in the organisation. */
interface HolidayDraft {
  date: string;
  name: string;
  states: string[];
}

const EMPTY_DRAFT: HolidayDraft = { date: '', name: '', states: [] };

const errorMessage = (err: unknown, fallback: string) =>
  (err as { message?: string })?.message || fallback;

interface HolidayFieldsProps {
  idPrefix: string;
  draft: HolidayDraft;
  onChange: (draft: HolidayDraft) => void;
  stateOptions: readonly string[];
  onSave: () => void;
  onCancel: () => void;
  saving: boolean;
  error: string;
  /** Shown under the fields; the edit form uses it to say what a change does not touch. */
  note?: string;
}

function HolidayFields({ idPrefix, draft, onChange, stateOptions, onSave, onCancel, saving, error, note }: HolidayFieldsProps) {
  const toggleState = (code: string) => {
    const next = draft.states.includes(code)
      ? draft.states.filter(s => s !== code)
      : [...draft.states, code];
    onChange({ ...draft, states: next });
  };

  const chip = (active: boolean) => cn(
    'px-3 py-1.5 rounded-full text-sm font-medium border transition-colors',
    active
      ? 'bg-primary-600 border-primary-600 text-white'
      : 'bg-white border-neutral-300 text-neutral-700 hover:border-neutral-400'
  );

  return (
    <div className="p-4 bg-gray-50 rounded-lg border border-neutral-200 space-y-4">
      <div className="flex flex-wrap items-end gap-3">
        <div>
          <label htmlFor={`${idPrefix}-date`} className="block text-sm font-medium text-neutral-700 mb-1">Date</label>
          <input
            id={`${idPrefix}-date`}
            type="date"
            value={draft.date}
            onChange={e => onChange({ ...draft, date: e.target.value })}
            className="border border-neutral-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          />
        </div>
        <div className="flex-1 min-w-[200px]">
          <label htmlFor={`${idPrefix}-name`} className="block text-sm font-medium text-neutral-700 mb-1">Name</label>
          <input
            id={`${idPrefix}-name`}
            type="text"
            value={draft.name}
            onChange={e => onChange({ ...draft, name: e.target.value })}
            placeholder="e.g. Company Shutdown Day"
            className="w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          />
        </div>
      </div>

      <div>
        <div className="text-sm font-medium text-neutral-700 mb-1">Applies to</div>
        <div className="flex flex-wrap gap-2" role="group" aria-label="Applies to">
          <button
            type="button"
            aria-pressed={draft.states.length === 0}
            onClick={() => onChange({ ...draft, states: [] })}
            className={chip(draft.states.length === 0)}
          >
            Everyone
          </button>
          {stateOptions.map(code => (
            <button
              key={code}
              type="button"
              aria-pressed={draft.states.includes(code)}
              onClick={() => toggleState(code)}
              className={chip(draft.states.includes(code))}
            >
              {code}
            </button>
          ))}
        </div>
        <p className="text-xs text-neutral-500 mt-2">
          {draft.states.length === 0
            ? 'Every employee gets this holiday, whatever state is on their profile.'
            : `Only employees whose profile state is ${draft.states.join(', ')} get this holiday.`}
        </p>
      </div>

      {note && <p className="text-xs text-neutral-500">{note}</p>}
      {error && <p className="text-sm text-red-700">{error}</p>}

      <div className="flex flex-wrap gap-2">
        <Button onClick={onSave} disabled={saving || !draft.date || !draft.name.trim()} variant="primary" size="sm">
          {saving ? 'Saving...' : 'Save'}
        </Button>
        <Button onClick={onCancel} disabled={saving} variant="outline" size="sm">
          Cancel
        </Button>
      </div>
    </div>
  );
}

export default function PublicHolidaySettings() {
  const orgTz = useOrgTimezone();
  const { hasAccess: hasHRAccess, loading: hrLoading } = useModuleLicense('timesheet', 'hr');
  const [holidays, setHolidays] = useState<PublicHoliday[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedYear, setSelectedYear] = useState(CURRENT_YEAR);
  const [states, setStates] = useState<string[]>([]);

  // Add form
  const [addDraft, setAddDraft] = useState<HolidayDraft>(EMPTY_DRAFT);
  const [showAddForm, setShowAddForm] = useState(false);

  // Edit form: one row at a time, in place of the row
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editDraft, setEditDraft] = useState<HolidayDraft>(EMPTY_DRAFT);

  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState('');

  // Seed form
  const [seedState, setSeedState] = useState('');
  const [seeding, setSeeding] = useState(false);

  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const loadHolidays = useCallback(async () => {
    try {
      setLoading(true);
      const res = await publicHolidayService.getHolidays({ year: selectedYear });
      if (res.success) {
        setHolidays(res.data);
      }
    } catch {
      setError('Failed to load public holidays');
    } finally {
      setLoading(false);
    }
  }, [selectedYear]);

  const loadStates = useCallback(async () => {
    try {
      const res = await publicHolidayService.getStates();
      if (res.success) {
        setStates(res.data);
      }
    } catch {
      // non-critical
    }
  }, []);

  useEffect(() => {
    loadHolidays();
  }, [loadHolidays]);

  useEffect(() => {
    loadStates();
  }, [loadStates]);

  const handleAdd = async () => {
    if (!addDraft.date || !addDraft.name.trim()) return;
    setFormError('');
    setSaving(true);
    try {
      const res = await publicHolidayService.addHoliday({
        date: addDraft.date,
        name: addDraft.name.trim(),
        states: addDraft.states,
      });
      if (res.success) {
        setSuccess('Holiday added');
        setAddDraft(EMPTY_DRAFT);
        setShowAddForm(false);
        loadHolidays();
        setTimeout(() => setSuccess(''), 3000);
      } else {
        setFormError(res.error?.message || res.message || 'Failed to add holiday');
      }
    } catch (err) {
      setFormError(errorMessage(err, 'Failed to add holiday'));
    } finally {
      setSaving(false);
    }
  };

  const startEdit = (h: PublicHoliday) => {
    setFormError('');
    setShowAddForm(false);
    setEditingId(h.id);
    setEditDraft({ date: h.date, name: h.name, states: h.state ? [h.state.toUpperCase()] : [] });
  };

  const handleSaveEdit = async () => {
    if (editingId === null || !editDraft.date || !editDraft.name.trim()) return;
    setFormError('');
    setSaving(true);
    try {
      const res = await publicHolidayService.updateHoliday(editingId, {
        date: editDraft.date,
        name: editDraft.name.trim(),
        states: editDraft.states,
      });
      if (res.success) {
        setSuccess('Holiday updated');
        setEditingId(null);
        loadHolidays();
        setTimeout(() => setSuccess(''), 3000);
      } else {
        setFormError(res.error?.message || res.message || 'Failed to update holiday');
      }
    } catch (err) {
      setFormError(errorMessage(err, 'Failed to update holiday'));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: number) => {
    setError('');
    try {
      const res = await publicHolidayService.removeHoliday(id);
      if (res.success) {
        setHolidays(prev => prev.filter(h => h.id !== id));
        setSuccess('Holiday removed');
        setTimeout(() => setSuccess(''), 3000);
      } else {
        setError(res.error?.message || res.message || 'Failed to remove holiday');
      }
    } catch (err) {
      setError(errorMessage(err, 'Failed to remove holiday'));
    }
  };

  const handleSeed = async () => {
    setSeeding(true);
    setError('');
    try {
      // Empty state → seed national + every state (so any employee's state is covered).
      const res = await publicHolidayService.seedHolidays({ state: seedState || undefined, year: selectedYear });
      if (res.success) {
        setSuccess(`Seeded ${res.data.inserted} holiday(s)`);
        loadHolidays();
        setTimeout(() => setSuccess(''), 3000);
      }
    } catch {
      setError('Failed to seed holidays');
    } finally {
      setSeeding(false);
    }
  };

  const formatDate = (dateStr: string) => {
    return formatLocalDateInTz(dateStr, orgTz);
  };

  // Who gets the holiday. "Custom" is a separate badge: it says where the row
  // came from, and a custom holiday still applies to a state or to everyone.
  const getBadge = (h: PublicHoliday) => {
    if (h.state) return { label: h.state, className: 'bg-amber-100 text-amber-700' };
    if (h.is_national) return { label: 'National', className: 'bg-blue-100 text-blue-700' };
    return { label: 'Everyone', className: 'bg-gray-100 text-gray-700' };
  };

  const stateOptions = states.length > 0 ? states : AUSTRALIAN_STATES;

  if (hrLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <p className="text-neutral-500">Loading…</p>
      </div>
    );
  }

  if (!hasHRAccess) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <CalendarDays className="h-12 w-12 text-neutral-300 mx-auto mb-4" />
          <h2 className="text-lg font-semibold text-neutral-700">Access Restricted</h2>
          <p className="text-sm text-neutral-500 mt-1">A Timesheet HR license seat is required to manage public holidays.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <PageHeader
        title="Public Holidays"
        subtitle="Manage public holidays for your organisation"
        icon={<CalendarDays className="h-8 w-8 text-primary-600" />}
      />

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-6">
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700 text-sm">{error}</div>
        )}
        {success && (
          <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-green-700 text-sm">{success}</div>
        )}

        {/* Seed Section */}
        <div className="bg-white border border-neutral-200 rounded-xl p-6">
          <h2 className="text-lg font-semibold text-neutral-900 mb-4">Seed Australian Holidays</h2>
          <p className="text-sm text-neutral-500 mb-4">
            Import national and state-specific public holidays from the Australian calendar.
            Seeding <span className="font-medium">All states</span> is recommended so employees
            in any state get the correct holidays — each employee's state is set on their profile.
          </p>
          <div className="flex flex-wrap items-end gap-3">
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-1">State</label>
              <select
                value={seedState}
                onChange={e => setSeedState(e.target.value)}
                className="border border-neutral-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              >
                <option value="">All states (recommended)</option>
                {states.map(s => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-1">Year</label>
              <select
                value={selectedYear}
                onChange={e => setSelectedYear(parseInt(e.target.value))}
                className="border border-neutral-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              >
                {[CURRENT_YEAR - 1, CURRENT_YEAR, CURRENT_YEAR + 1].map(y => (
                  <option key={y} value={y}>{y}</option>
                ))}
              </select>
            </div>
            <Button onClick={handleSeed} disabled={seeding} variant="primary" size="sm">
              <Download className="h-4 w-4 mr-1" />
              {seeding ? 'Seeding...' : 'Seed Holidays'}
            </Button>
          </div>
        </div>

        {/* Holiday List */}
        <div className="bg-white border border-neutral-200 rounded-xl p-6">
          <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
            <h2 className="text-lg font-semibold text-neutral-900">
              Holidays — {selectedYear}
            </h2>
            <Button
              onClick={() => {
                setFormError('');
                setEditingId(null);
                setShowAddForm(!showAddForm);
              }}
              variant="outline"
              size="sm"
            >
              <Plus className="h-4 w-4 mr-1" />
              Add Holiday
            </Button>
          </div>

          {showAddForm && (
            <div className="mb-4">
              <HolidayFields
                idPrefix="add-holiday"
                draft={addDraft}
                onChange={setAddDraft}
                stateOptions={stateOptions}
                onSave={handleAdd}
                onCancel={() => setShowAddForm(false)}
                saving={saving}
                error={formError}
              />
            </div>
          )}

          {loading ? (
            <div className="text-center py-8 text-neutral-400">Loading...</div>
          ) : holidays.length === 0 ? (
            <div className="text-center py-8 text-neutral-400">
              No public holidays configured for {selectedYear}. Seed from the Australian calendar above or add manually.
            </div>
          ) : (
            <div className="divide-y divide-neutral-100">
              {holidays.map(h => {
                if (editingId === h.id) {
                  return (
                    <div key={h.id} className="py-3">
                      <HolidayFields
                        idPrefix={`edit-holiday-${h.id}`}
                        draft={editDraft}
                        onChange={setEditDraft}
                        stateOptions={stateOptions}
                        onSave={handleSaveEdit}
                        onCancel={() => setEditingId(null)}
                        saving={saving}
                        error={formError}
                        note="Public holiday entries already on timesheets are not changed."
                      />
                    </div>
                  );
                }
                const badge = getBadge(h);
                return (
                  <div key={h.id} className="flex items-start justify-between gap-3 py-3">
                    <div className="min-w-0 flex flex-wrap items-center gap-x-3 gap-y-1">
                      <div className="text-sm font-medium text-neutral-900 sm:w-28 shrink-0">
                        {formatDate(h.date)}
                      </div>
                      <div className="text-sm text-neutral-700 min-w-0 break-words">{h.name}</div>
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${badge.className}`}>
                        {badge.label}
                      </span>
                      {h.is_custom && (
                        <span className="text-xs px-2 py-0.5 rounded-full font-medium bg-purple-100 text-purple-700">
                          Custom
                        </span>
                      )}
                    </div>
                    <div className="flex shrink-0 gap-1">
                      <button
                        onClick={() => startEdit(h)}
                        className="text-neutral-400 hover:text-primary-600 transition-colors p-1"
                        title="Edit holiday"
                        aria-label={`Edit ${h.name}`}
                      >
                        <Pencil className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(h.id)}
                        className="text-neutral-400 hover:text-red-500 transition-colors p-1"
                        title="Remove holiday"
                        aria-label={`Remove ${h.name}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
